<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import Pill from "@components/Pill.svelte";
	import { PillColor, PillSize } from "@components/PillProps.js";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getEcommercePath } from "@core/schema/paths/getEcommercePath.js";
	import { getSolutionsPath } from "@core/schema/paths/getSolutionsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getBenefitsCrm } from "../data/getBenefitsCrm.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getCrmHowDev } from "../data/getCrmHowDev.js";
	import { getFaqsCrm } from "../data/getFaqsCrm.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getServicesCrm } from "../data/getServicesCrm.js";
	import type { CrmProps } from "./CrmProps.js";

	const { tenant } = getGlobalTemplateProps();
	const { partners }: CrmProps = $props();
	const crmFeatures = [
		"Správa kontaktů zákazníků",
		"Správa prodejního procesu",
		"Správa aktivit a úkolů",
		"Automatizace marketingu",
		"Sledování zákaznické podpory",
		"Reporty a analytika",
		"Správa produktů a ceníků",
		"Správa dokumentů a souborů",
		"Mobilní přístup a aplikace",
		"Segmentace a personalizace",
	];
</script>

<Layout withStructuredData>
	<Section
		paddingFollowNext
		background={Background.LightGradient}
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		hero
	>
		{#snippet headline()}
			<Formatted text="Zvyšte cg[[spokojenost zákazníků]] díky CRM na míru" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Už žádné ztracené kontakty, chaotické tabulky a neefektivní přístup k servisu zákazníků.]] Na zakázku vám vyvineme, optimalizujeme, integrujeme i automatizujeme profesionální CRM [{getSolutionsPath(
						tenant,
					)}][[řešení]]."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci nezávaznou konzultaci" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Se CRM na míru začnete vztahy se zákazníky řídit cg[[efektivně]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Na spolehlivém CRM (Customer Relationship Management) systému, přizpůsobeném potřebám firmy, často stojí a padá úspěch podnikání. Získáte díky němu komplexní kontrolu nad řízením vztahů se zákazníky, sp[[ale také vám pomůže zlepšit obchodní procesy i péči o klienty, a tím pádem i zvýšit prodeje.]]"
				/>
			</p>
			<p>
				<Formatted
					text="Ať už plánujete pořízení svého prvního CRM nebo potřebujete optimalizovat či automatizovat vaše současné řešení, tým Fastest je vám k ruce. sp[[CRM děláme například pro firmy z oblasti [{getEcommercePath(
						tenant,
					)}][[e-commerce]],]] sp[[výroby, HR, ale nebo logistiky a přepravy.]] "
				/>
			</p>
		{/snippet}
	</Section>

	<Section background={Background.LightGradient} noPadding="bot" gridHeadline="container-8 lg:text-center">
		{#snippet headline()}
			<Formatted text="S čím vám cg[[dokážeme]] pomoci?" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getServicesCrm(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Zeptat se na podrobnosti" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Vybavíme CRM funkcemi, které cg[[zrychlí růst]] vašeho byznysu" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Vaše podnikání je specifické a unikátní, tomu by měl odpovídat i CRM systém. Zapracujeme do něj funkce, které vám usnadní každodenní procesy a ušetří čas."
				/>
			</p>
			<ul class="flex gap-1 sm:gap-2 flex-wrap">
				{#each crmFeatures as label (label)}
					<Pill size={PillSize.Small} color={PillColor.White}>{label}</Pill>
				{/each}
			</ul>
		{/snippet}
	</Section>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[6 výhod]] CRM systému na míru" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getBenefitsCrm(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section background={Background.LightGradient} centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Komu už jsme cg[[pomohli]]?" />
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Crm)} />
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Chci fastest služby
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="Jak vypadá cg[[proces]] vývoje a implementace CRM" />
			<span class="block text-2md font-bold mt-6">
				<Formatted
					text="Provedeme vás celým procesem od úvodní cg[[analýzy]] až po cg[[implementaci]] a cg[[údržbu]] CRM systému."
				/>
			</span>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getCrmHowDev(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k cg[[CRM]]" />
		{/snippet}
		<FaqItems faqs={getFaqsCrm(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
